import { Tooltip, Typography } from '@material-ui/core';
import { FormattedRelativeTime } from '@superdispatch/dates';
import { Inline, Stack, Tag } from '@superdispatch/ui';
import { isEmpty } from 'shared/helpers/CommonHelpers';
import { useFlag } from 'shared/settings/FeatureToggles';
import { PostingCarrierLoadDTO } from '../../data/dto/PostingDTO';
import { getCarrierPostingDetails } from './CarrierPostingDetails';
import { EditedByShipperTag } from './EditedByShipperTag';
import { PostingDeclineReason } from './PostingDeclineReason';
import { PostingEditedChanges } from './PostingEditedChanges';

interface CarrierPostingStatusInfoProps {
  showShipperDeclineReason?: boolean;
  showEditedByShipper?: boolean;
  carrierPostingInfo: PostingCarrierLoadDTO;
}

interface RelativeTimeTextProps {
  label: string;
  date: string;
}

export function CarrierPostingStatusInfo({
  carrierPostingInfo,
  showShipperDeclineReason,
  showEditedByShipper,
}: CarrierPostingStatusInfoProps) {
  const isEditedByShipperFlagEnabled = useFlag('slb_edited_by_shipper');
  const { color, text, variant } = getCarrierPostingDetails(carrierPostingInfo);
  const isShipperDeclined =
    carrierPostingInfo.status === 'REQUEST_DECLINED_BY_BROKER';
  const visibleLoadEditedAt =
    isEditedByShipperFlagEnabled && showEditedByShipper
      ? carrierPostingInfo.load_edited_at
      : null;

  const declineText = isEmpty(carrierPostingInfo.broker_decline_reason)
    ? 'No Reason Specified'
    : String(carrierPostingInfo.broker_decline_reason);

  const statusTag = (
    <Tooltip
      title={showShipperDeclineReason || !isShipperDeclined ? '' : declineText}
    >
      <Tag color={color} variant={variant}>
        <Typography aria-label="Carrier posting status" variant="h5">
          {text}
        </Typography>
      </Tag>
    </Tooltip>
  );

  const statusChangedDate = carrierPostingInfo.changed_at && (
    <RelativeTimeText
      label="Carrier posting status changed date"
      date={carrierPostingInfo.changed_at}
    />
  );

  return (
    <Stack>
      <Inline verticalAlign={visibleLoadEditedAt ? 'center' : 'top'}>
        {statusTag}
        {statusChangedDate}
      </Inline>

      {visibleLoadEditedAt && (
        <>
          <Inline verticalAlign="center">
            <EditedByShipperTag />
            <RelativeTimeText
              label="Load edited date"
              date={visibleLoadEditedAt}
            />
          </Inline>

          <PostingEditedChanges loadEdits={carrierPostingInfo.load_edits} />
        </>
      )}

      {showShipperDeclineReason &&
        isShipperDeclined &&
        !isEmpty(carrierPostingInfo.broker_decline_reason) && (
          <PostingDeclineReason
            declineReasonText={carrierPostingInfo.broker_decline_reason}
          />
        )}
    </Stack>
  );
}

function RelativeTimeText({ label, date }: RelativeTimeTextProps) {
  return (
    <Typography noWrap={true} aria-label={label}>
      <FormattedRelativeTime format="JodaISO" date={date} />
    </Typography>
  );
}
